import { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { fonts } from '../styles/theme';
import { useTheme } from '../context/ThemeContext';
import { getProfile, getUser, signOut } from '../utils/auth';
import { getUnreadCrmMessageSummary } from '../utils/crmV2Api';

const PRIMARY_MENU = [
  { label: 'Account', to: '/portal', match: ['/portal', '/portal/distributions', '/portal/deposit', '/portal/account', '/portal/messages'], section: 'account' },
  { label: 'Explainers', to: '/portal/explainers', match: ['/portal/explainers/cash-secured-puts', '/portal/explainers/cnrf-insurance-vs-non-insured', '/portal/explainers'], section: 'explainers' },
];

const SECONDARY_MENU = [
  { label: 'Holdings', to: '/portal', section: 'account' },
  { label: 'Distributions', to: '/portal/distributions', section: 'account' },
  { label: 'Deposit', to: '/portal/deposit', section: 'account' },
  { label: 'Messages', to: '/portal/messages', section: 'account' },
  { label: 'How the Fund Generates Income', to: '/portal/explainers/cash-secured-puts', section: 'explainers' },
  { label: 'How Insurance Works', to: '/portal/explainers/cnrf-insurance-vs-non-insured', section: 'explainers' },
];

const getSection = (pathname) => PRIMARY_MENU.find((item) => item.match.includes(pathname))?.section || 'account';

const SunIcon = ({ color }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2" />
    <path d="M12 20v2" />
    <path d="m4.93 4.93 1.41 1.41" />
    <path d="m17.66 17.66 1.41 1.41" />
    <path d="M2 12h2" />
    <path d="M20 12h2" />
    <path d="m6.34 17.66-1.41 1.41" />
    <path d="m19.07 4.93-1.41 1.41" />
  </svg>
);

const MoonIcon = ({ color }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20.5 14.2A8.5 8.5 0 0 1 9.8 3.5 8.5 8.5 0 1 0 20.5 14.2Z" />
  </svg>
);

const MessagePulseStyle = () => (
  <style>{`
    @keyframes cnrMessagePulse {
      0%, 100% { box-shadow: 0 0 0 0 rgba(88, 166, 255, 0.42); transform: translateY(0); }
      50% { box-shadow: 0 0 0 7px rgba(88, 166, 255, 0); transform: translateY(-1px); }
    }
  `}</style>
);

const InvestorLayout = ({ children }) => {
  const location = useLocation();
  const { colors, mode, toggleTheme } = useTheme();
  const [profileName, setProfileName] = useState('Member');
  const [profileAlert, setProfileAlert] = useState('');
  const [unreadMessages, setUnreadMessages] = useState({ count: 0, latestThreadId: null });
  const section = getSection(location.pathname);
  const secondaryItems = useMemo(() => SECONDARY_MENU.filter((item) => item.section === section), [section]);

  useEffect(() => {
    const load = async () => {
      const [profile, user] = await Promise.all([getProfile(), getUser()]);
      if (profile?.username) setProfileName(profile.username);
      const missing = [];
      if (!user?.email) missing.push('email');
      if (!profile?.phone) missing.push('phone');
      setProfileAlert(missing.length ? `Please add your ${missing.join(' and ')} in Profile.` : '');
    };
    load();
  }, []);

  useEffect(() => {
    let mounted = true;
    const loadUnread = async () => {
      try {
        const summary = await getUnreadCrmMessageSummary({ manager: false });
        if (mounted) setUnreadMessages(summary);
      } catch {
        if (mounted) setUnreadMessages({ count: 0, latestThreadId: null });
      }
    };
    loadUnread();
    const id = setInterval(loadUnread, 30000);
    window.addEventListener('focus', loadUnread);
    return () => {
      mounted = false;
      clearInterval(id);
      window.removeEventListener('focus', loadUnread);
    };
  }, []);

  const headerBg = mode === 'dark' ? 'rgba(22,27,34,0.96)' : 'rgba(255,255,255,0.96)';
  const navText = mode === 'dark' ? '#8b949e' : '#4a6074';
  const navTextDim = mode === 'dark' ? '#6e7681' : '#607487';
  const logoText = mode === 'dark' ? '#f4f7fb' : '#0d3157';
  const logoSubtle = mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(13,49,87,0.08)';

  return (
    <div style={{ minHeight: '100vh', background: colors.bg, color: colors.textMain, fontFamily: fonts.main }}>
      <MessagePulseStyle />
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 20,
          background: headerBg,
          borderBottom: `1px solid ${colors.borderSub}`,
          backdropFilter: 'blur(10px)',
        }}
      >
        <div style={{ maxWidth: 1600, margin: '0 auto', padding: '0 14px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              minHeight: 54,
              gap: 16,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 18, minWidth: 0 }}>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  flexShrink: 0,
                }}
              >
                <span
                  style={{
                    color: logoText,
                    fontFamily: fonts.display,
                    fontSize: 24,
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    lineHeight: 1,
                    textTransform: 'uppercase',
                    borderBottom: `1px solid ${logoSubtle}`,
                    paddingBottom: 1,
                  }}
                >
                  CNR Family
                </span>
              </div>

              <div
                style={{
                  display: 'flex',
                  gap: 24,
                  fontSize: 13,
                  color: navText,
                  overflowX: 'auto',
                  whiteSpace: 'nowrap',
                  fontFamily: fonts.main,
                }}
              >
                {PRIMARY_MENU.map((item) => {
                  const active = item.match.includes(location.pathname);
                  return (
                    <NavLink
                      key={item.label}
                      to={item.to}
                      style={{
                        textDecoration: 'none',
                        color: active ? colors.accent : navText,
                        fontSize: 13,
                        fontWeight: active ? 600 : 400,
                        borderBottom: active ? `2px solid ${colors.accent}` : '2px solid transparent',
                        padding: '0 0 2px',
                        cursor: 'pointer',
                      }}
                    >
                      {item.label}
                    </NavLink>
                  );
                })}
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
              <div style={{ color: navTextDim, fontSize: 12 }}>
                {profileName}
              </div>
              <NavLink
                to="/portal/account"
                style={({ isActive }) => ({
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  background: 'transparent',
                  border: 'none',
                  padding: '4px 8px',
                  color: isActive ? colors.accent : navText,
                  fontFamily: fonts.main,
                  fontSize: 13,
                  fontWeight: isActive ? 600 : 400,
                  textDecoration: 'none',
                })}
              >
                Profile
              </NavLink>
              {unreadMessages.count > 0 && (
                <NavLink
                  to="/portal/messages"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: `1px solid ${colors.accent}`,
                    borderRadius: 999,
                    padding: '5px 10px',
                    color: colors.accent,
                    fontSize: 12,
                    fontWeight: 700,
                    textDecoration: 'none',
                    animation: 'cnrMessagePulse 1.35s ease-in-out infinite',
                  }}
                >
                  You have a new message
                </NavLink>
              )}
              <button
                type="button"
                onClick={toggleTheme}
                title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 28,
                  height: 28,
                  borderRadius: 999,
                  border: `1px solid ${colors.borderSub}`,
                  background: colors.surface2,
                  color: navText,
                  cursor: 'pointer',
                  fontSize: 13,
                  lineHeight: 1,
                }}
              >
                {mode === 'dark' ? <SunIcon color={navText} /> : <MoonIcon color={navText} />}
              </button>
              <button
                onClick={async () => {
                  await signOut();
                  window.location.hash = '#/investor-login';
                }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  background: 'transparent',
                  border: 'none',
                  padding: '4px 8px',
                  color: navText,
                  fontFamily: fonts.main,
                  fontSize: 13,
                  fontWeight: 400,
                  cursor: 'pointer',
                }}
              >
                Log Out
              </button>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              gap: 0,
              overflowX: 'auto',
              whiteSpace: 'nowrap',
              padding: '0',
            }}
          >
            {secondaryItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end
                style={({ isActive }) => ({
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '11px 14px 10px',
                  borderBottom: isActive ? `2px solid ${colors.accent}` : '2px solid transparent',
                  color: isActive ? colors.accent : navTextDim,
                  fontSize: 12,
                  fontWeight: isActive ? 600 : 400,
                  textDecoration: 'none',
                })}
              >
                {item.label}
              </NavLink>
            ))}
          </div>
        </div>
      </div>

      {profileAlert ? (
        <NavLink
          to="/portal/account"
          style={{
            display: 'block',
            maxWidth: 1600,
            margin: '12px auto 0',
            padding: '10px 16px',
            background: colors.goldDim,
            color: colors.warning,
            border: `1px solid ${colors.gold}`,
            textDecoration: 'none',
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          {profileAlert}
        </NavLink>
      ) : null}

      <main>{children}</main>
    </div>
  );
};

export default InvestorLayout;
